import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { LprIntegrationType } from '@lib/data-dog-rum/custom-action-map';

import { SnLprVigilantRead } from '../api/models/lpr-vigilant-read';
import {
  extractLprErrorCode,
  extractLprRawError,
  parseLprApiError,
} from '../utils/lpr-error-utils';

@Injectable({
  providedIn: 'root',
})
export class VigilantLprService implements OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly _integrationType: LprIntegrationType = 'vigilant_ps';

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _metrics: FsDataDogRumService,
  ) {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public getVigilantPlates(params: ISnListQueryParameters): Observable<any> {
    // vigilant returns all results in one page, so we need to handle pagination
    if (params.page > 1) {
      return of({
        results: [],
      });
    }
    const startTime = Date.now();
    const payload = {};
    let fromDate;
    let toDate;
    if (params.event_timestamp_from || params.event_timestamp_to) {
      fromDate = params.event_timestamp_from
        ? format(new Date(params.event_timestamp_from), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX")
        : null;

      toDate = params.event_timestamp_to
        ? format(new Date(params.event_timestamp_to), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX")
        : null;
    } else {
      fromDate = null;
      toDate = null;
    }

    payload['from_date'] = fromDate;
    payload['to_date'] = toDate;
    payload['plate'] = params.plate || null;
    payload['in_bbox'] = params.in_bbox || null;

    const payloadParams = this._preparePayload(payload);

    return this._httpClient
      .get('/api/integrations/vigilant_ps/plate_search/', {
        params: payloadParams,
      })
      .pipe(
        map((response: any) => {
          const results = response.results.map((read) => new SnLprVigilantRead(read));
          const count = results.length || 0;
          const result = {
            count,
            totalPages: response.totalPages,
            results,
            isVigilant: true,
          };

          this._metrics.addAction('lpr_search', {
            status: response.status || 200,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
          });

          return result;
        }),
        catchError((err: HttpErrorResponse) => {
          this._metrics.addAction('lpr_search_error', {
            status: err?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
            raw_error: extractLprRawError(err),
            error_code: extractLprErrorCode(err),
            error_message: parseLprApiError(err),
          });

          if (err.error.plate) {
            const toastMessage = `
        <strong><u>VIGILANT: </u></strong>Please search exact plate number.
      `;
            this._toastrService.error(toastMessage, null, {
              enableHtml: true,
            });
          }
          if (params.include_flock || params.include_eagleEye) {
            return of({ count: 0, results: [], skipError: true });
          }
          return throwError(err);
        }),
      );
  }

  public destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _preparePayload(payload: any): any {
    Object.keys(payload).forEach((key) => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    return {
      ...payload,
    };
  }
}
